import React from 'react';
import { Card, CardContent } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import { FlameIcon, ClockIcon, CalendarIcon, TrendingUpIcon, ZapIcon, UsersIcon } from 'lucide-react';
import type { PeakDay, PeakHour, ChannelBreakdown } from '../../lib/metrics-hooks';
interface MetricsPeakInsightsProps {
  peakDays: PeakDay[];
  peakHours: PeakHour[];
  channelBreakdown: ChannelBreakdown;
  totalOrders: number;
  totalRevenue: number;
  averageTicket: number;
}
export function MetricsPeakInsights({
  peakDays,
  peakHours,
  channelBreakdown,
  totalOrders,
  totalRevenue,
  averageTicket
}: MetricsPeakInsightsProps) {
  const totalChannel = channelBreakdown.qr + channelBreakdown.waiter + channelBreakdown.other;
  const qrPct = totalChannel > 0 ? Math.round(channelBreakdown.qr / totalChannel * 100) : 0;
  const waiterPct = totalChannel > 0 ? Math.round(channelBreakdown.waiter / totalChannel * 100) : 0;
  return <div className="space-y-4" data-id="element-808">
      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-809">
        <Card data-id="element-810">
          <CardContent className="p-5" data-id="element-811">
            <div className="flex items-start gap-3" data-id="element-812">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-813">
                <TrendingUpIcon className="w-5 h-5 text-emerald-600" data-id="element-814" />
              </div>
              <div className="min-w-0" data-id="element-815">
                <p className="text-sm text-gray-500 font-medium" data-id="element-816">
                  Ingresos Totales
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-817">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-818">
          <CardContent className="p-5" data-id="element-819">
            <div className="flex items-start gap-3" data-id="element-820">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-821">
                <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-822" />
              </div>
              <div className="min-w-0" data-id="element-823">
                <p className="text-sm text-gray-500 font-medium" data-id="element-824">
                  Total Órdenes
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5" data-id="element-825">
                  {totalOrders}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-826">
          <CardContent className="p-5" data-id="element-827">
            <div className="flex items-start gap-3" data-id="element-828">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-829">
                <ZapIcon className="w-5 h-5 text-purple-600" data-id="element-830" />
              </div>
              <div className="min-w-0" data-id="element-831">
                <p className="text-sm text-gray-500 font-medium" data-id="element-832">
                  Ticket Promedio
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-833">
                  {formatCurrency(averageTicket)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peak Insights Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-834">
        {/* Peak Day */}
        <Card data-id="element-835">
          <CardContent className="p-5" data-id="element-836">
            <div className="flex items-center gap-2 mb-3" data-id="element-837">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center" data-id="element-838">
                <FlameIcon className="w-4 h-4 text-red-500" data-id="element-839" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-840">
                Día Pico
              </span>
            </div>
            {peakDays.length > 0 ? <div className="space-y-2" data-id="element-841">
                {peakDays.map((day, i) => <div key={day.date} className="flex items-center justify-between" data-id="element-842">
                    <div className="flex items-center gap-2" data-id="element-843">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-844">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-845">{day.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-846">
                      {day.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-847">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Peak Hour */}
        <Card data-id="element-848">
          <CardContent className="p-5" data-id="element-849">
            <div className="flex items-center gap-2 mb-3" data-id="element-850">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-851">
                <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-852" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-853">
                Hora Pico
              </span>
            </div>
            {peakHours.length > 0 ? <div className="space-y-2" data-id="element-854">
                {peakHours.map((hour, i) => <div key={hour.hour} className="flex items-center justify-between" data-id="element-855">
                    <div className="flex items-center gap-2" data-id="element-856">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-857">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-858">
                        {hour.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-859">
                      {hour.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-860">Sin datos</p>}
          </CardContent>
        </Card>

        {/* QR Self-service */}
        <Card data-id="element-861">
          <CardContent className="p-5" data-id="element-862">
            <div className="flex items-center gap-2 mb-3" data-id="element-863">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-864">
                <CalendarIcon className="w-4 h-4 text-emerald-600" data-id="element-865" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-866">
                Autoservicio QR
              </span>
            </div>
            <div className="space-y-1" data-id="element-867">
              <p className="text-2xl font-bold text-emerald-600" data-id="element-868">
                {channelBreakdown.qr}
              </p>
              <p className="text-sm text-gray-500" data-id="element-869">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-870">{qrPct}%</span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-871">
                {formatCurrency(channelBreakdown.qrRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Waiter */}
        <Card data-id="element-872">
          <CardContent className="p-5" data-id="element-873">
            <div className="flex items-center gap-2 mb-3" data-id="element-874">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-875">
                <UsersIcon className="w-4 h-4 text-blue-600" data-id="element-876" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-877">
                Mesero
              </span>
            </div>
            <div className="space-y-1" data-id="element-878">
              <p className="text-2xl font-bold text-blue-600" data-id="element-879">
                {channelBreakdown.waiter}
              </p>
              <p className="text-sm text-gray-500" data-id="element-880">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-881">
                  {waiterPct}%
                </span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-882">
                {formatCurrency(channelBreakdown.waiterRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}